// /inbox — 统一收件箱 (v1.38)
//
// 背景: 用户是 1 人外贸运营, 原来「读信(Lark) / 翻译(飞书 bot) / 记录(Trade OS) /
// 按发送(邮件客户端)」被劈成 4 个地方。这个页面把前 3 件收进 Trade OS 一处,
// 只有最后「按发送」留在 Lark (企业邮箱挂在 Lark 上, 不能动 MX / DNS, 0 成本约束)。
//
// 入站: Lark IMAP (Mac 本地同步器) → POST /api/inbound/email → D1 → GET /api/inbox
// 出站: Trade OS AI 出稿 → 复制正文/主题 → 在 Lark 桌面端发送
//
// 桌面 (≥1024px) 两栏: 左线程列表 / 右消息+出站框; 移动端全屏切换 (列表 ↔ 消息)。
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Skeleton } from '../components/Skeleton';
import { Button, Input, Select, Textarea } from '../components/ui';
import { type InboxMessage, type InboxThread, inboxApi, inquiryApi } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

export const Route = createFileRoute('/inbox')({
  component: InboxPage,
});

/** Lark 邮箱网页版 (租户域名) — 嵌入式浏览器打不开, 只能桌面端 */
const LARK_MAIL_URL = 'https://ijppvlabd7kb.jp.larksuite.com/mail/';

/** 收件箱筛选 tab */
type FilterKey = 'all' | 'unread' | 'pending' | 'overdue';

const FILTER_TABS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'unread', label: '未读' },
  { key: 'pending', label: '待回' },
  { key: 'overdue', label: '逾期' },
];

/** 出站目标语言 */
type DraftTarget = 'ja' | 'en' | 'zh';

const TARGET_OPTIONS = [
  { value: 'ja', label: '日文' },
  { value: 'en', label: '英文' },
  { value: 'zh', label: '中文' },
];

/** 目标语言中文名 (提示文案用) */
const TARGET_LABELS: Record<DraftTarget, string> = { ja: '日文', en: '英文', zh: '中文' };

/** toast 类型 */
type ToastKind = 'success' | 'error' | 'info';

interface ToastState {
  kind: ToastKind;
  msg: string;
}

/**
 * 相对时间: 刚刚 / N 分钟前 / N 小时前 / 昨天 / N 天前 / YYYY-MM-DD
 * @param ms 时间戳 (毫秒)
 */
function fmtRelative(ms: number): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return '刚刚';
  const min = Math.floor(diff / 60000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} 小时前`;
  const day = Math.floor(hour / 24);
  if (day === 1) return '昨天';
  if (day < 30) return `${day} 天前`;
  const d = new Date(ms);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * 消息时间戳 → "10/19 16:18"
 * @param ms 时间戳 (毫秒)
 */
function fmtMsgTime(ms: number): string {
  if (!ms) return '—';
  const d = new Date(ms);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${mi}`;
}

/**
 * 正文是否算「长文」(需要折叠)
 * @param body 正文原文
 */
function isLongBody(body: string): boolean {
  if (!body) return false;
  return body.length > 80 || body.split('\n').length > 4;
}

export function InboxPage() {
  const qc = useQueryClient();
  const currentCompanyId = useAuthStore((s) => s.currentCompanyId);

  // ---- 筛选 + 搜索 (搜索输入防抖 300ms, 避免每敲一个字打一次 API) ----
  const [filter, setFilter] = useState<FilterKey>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ---- 选中的线程 ----
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['inbox', currentCompanyId, filter, search],
    queryFn: () => inboxApi.list({ filter, search: search || undefined, limit: 100 }),
  });
  const threads = listQuery.data?.items ?? [];
  const counts = listQuery.data?.counts;
  const selectedThread = threads.find((t) => t.inquiryId === selectedId) ?? null;

  // ---- 线程内消息 (时间正序) ----
  const messagesQuery = useQuery({
    queryKey: ['inquiry-messages', selectedId],
    queryFn: () => inquiryApi.messages(selectedId ?? ''),
    enabled: !!selectedId,
  });
  const messages = [...(messagesQuery.data?.items ?? [])].sort((a, b) => a.createdAt - b.createdAt);

  // ---- 询盘详情 (拿 customer 信息) ----
  const inquiryQuery = useQuery({
    queryKey: ['inquiry', selectedId],
    queryFn: () => inquiryApi.get(selectedId ?? ''),
    enabled: !!selectedId,
  });

  // ---- 单条消息状态: 展开 / 本地已读 / 译文 ----
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [translatingIds, setTranslatingIds] = useState<Record<string, boolean>>({});
  const markedRef = useRef<Set<string>>(new Set());

  // ---- 出站框 ----
  const [composeText, setComposeText] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [target, setTarget] = useState<DraftTarget>('ja');
  const [copied, setCopied] = useState<'body' | 'subject' | null>(null);
  const copyTimer = useRef<number | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  // 切换线程时清空出站框 / 译文 / 展开态
  // biome-ignore lint/correctness/useExhaustiveDependencies: 只在 selectedId 变化时重置, effect 体内不直接读它
  useEffect(() => {
    setComposeText('');
    setComposeSubject('');
    setTranslations({});
    setTranslatingIds({});
    setExpandedIds(new Set());
  }, [selectedId]);

  // 复制按钮 1.5 秒后恢复文案
  useEffect(() => {
    return () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    };
  }, []);

  // 打开线程 → 把未读的来信标记为已读 (幂等; 失败不影响阅读)
  useEffect(() => {
    if (!selectedId) return;
    const targets = messages.filter(
      (m) => m.direction === 'inbound' && !m.isRead && !markedRef.current.has(m.id),
    );
    if (targets.length === 0) return;
    for (const m of targets) markedRef.current.add(m.id);
    setReadIds(new Set(markedRef.current));
    void Promise.all(targets.map((m) => inquiryApi.markMessageRead(selectedId, m.id)))
      .then(() => qc.invalidateQueries({ queryKey: ['inbox'] }))
      .catch(() => {
        // 静默失败: 标记已读不影响读信
      });
  }, [messages, selectedId, qc]);

  // ---- 单条来信翻译成中文 ----
  async function translateMessage(message: InboxMessage) {
    if (!selectedId) return;
    const text = message.bodyOriginal ?? '';
    if (!text.trim()) return;
    setTranslatingIds((prev) => ({ ...prev, [message.id]: true }));
    try {
      const res = await inquiryApi.translateText(selectedId, { target: 'zh', text });
      if (res.ok && res.translated) {
        setTranslations((prev) => ({ ...prev, [message.id]: res.translated }));
      } else {
        setToast({ kind: 'error', msg: res.error ?? '翻译失败' });
      }
    } catch (e) {
      setToast({ kind: 'error', msg: `翻译失败: ${(e as Error).message}` });
    } finally {
      setTranslatingIds((prev) => {
        const next = { ...prev };
        delete next[message.id];
        return next;
      });
    }
  }

  // ---- AI 出稿 (中文意图 → 目标语言) ----
  const draftMut = useMutation({
    mutationFn: () => inquiryApi.draft(selectedId ?? '', { text: composeText, target }),
    onSuccess: (d) => {
      setComposeText(d.body ?? '');
      setComposeSubject(d.subject ?? '');
      setToast({
        kind: 'success',
        msg: `AI 出稿完成 (${d.generatedBy === 'ai' ? 'AI' : '模板兜底'})`,
      });
    },
    onError: (e) => setToast({ kind: 'error', msg: `出稿失败: ${(e as Error).message}` }),
  });

  // ---- 出站框翻译 (中文 → 目标语言) ----
  const translateMut = useMutation({
    mutationFn: () => inquiryApi.translateText(selectedId ?? '', { target, text: composeText }),
    onSuccess: (res) => {
      if (res.ok && res.translated) {
        setComposeText(res.translated);
        setToast({ kind: 'success', msg: `已翻译成${TARGET_LABELS[target]}` });
      } else {
        setToast({ kind: 'error', msg: res.error ?? '翻译失败' });
      }
    },
    onError: (e) => setToast({ kind: 'error', msg: `翻译失败: ${(e as Error).message}` }),
  });

  // ---- 标记已回 ----
  const markMut = useMutation({
    mutationFn: () => inquiryApi.markReplied(selectedId ?? '', { method: 'email' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['inquiry', selectedId] });
      setToast({ kind: 'success', msg: '已标记为已回复' });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  // ---- 复制正文 / 复制主题 (按钮变「已复制」1.5 秒) ----
  async function copyToClipboard(kind: 'body' | 'subject', value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(null), 1500);
    } catch {
      setToast({ kind: 'error', msg: '复制失败, 请手动选择复制' });
    }
  }

  // ---- 在 Lark 打开 (嵌入式浏览器打不开, 提示用桌面端) ----
  function openLark() {
    window.open(LARK_MAIL_URL, '_blank');
    setToast({ kind: 'info', msg: 'Lark 邮箱在嵌入式浏览器打不开，请用桌面端' });
  }

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const customerName = selectedThread?.customerName ?? '';
  const customerCountry = selectedThread?.customerCountry ?? '';
  const threadSubject = inquiryQuery.data?.subject ?? selectedThread?.subject ?? '';

  return (
    <div className="space-y-4">
      {/* 页头 */}
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold mb-1">收件箱</h1>
        <p className="text-ink-3 text-xs sm:text-sm">
          读信 / 翻译 / 起草都在这里 · 邮箱本体仍在 Lark
          {counts ? ` · ${counts.all} 个会话` : ''}
        </p>
      </div>

      <div className="lg:grid lg:grid-cols-[320px_minmax(0,1fr)] lg:gap-4 lg:items-start">
        {/* 左栏: 线程列表 */}
        <div className={selectedId ? 'hidden lg:block' : 'block'}>
          <ThreadListPanel
            filter={filter}
            onFilterChange={setFilter}
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            threads={threads}
            counts={counts}
            activeId={selectedId}
            onSelect={setSelectedId}
            isLoading={listQuery.isLoading}
            isError={listQuery.isError}
            onRetry={() => void listQuery.refetch()}
          />
        </div>

        {/* 右栏: 消息 + 出站框 */}
        <div className={selectedId ? 'block' : 'hidden lg:block'}>
          {!selectedId ? (
            <div className="bg-surface border border-line rounded">
              <EmptyState
                icon="mail"
                title="选一个会话开始处理"
                description="左边选客户线程, 这里显示往来邮件, 可以直接翻译和起草回复"
              />
            </div>
          ) : (
            <div className="bg-surface border border-line rounded overflow-hidden">
              {/* 线程头 */}
              <div className="px-4 py-3 border-b border-line flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedId(null)}
                      className="lg:hidden shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs border border-line rounded text-ink-2 hover:bg-surface-2"
                    >
                      <Icon name="arrow-left" size="xs" />
                      返回
                    </button>
                    <h2 className="text-sm font-semibold text-ink truncate">{customerName}</h2>
                    {customerCountry && (
                      <span className="text-xs text-ink-3 shrink-0">· {customerCountry}</span>
                    )}
                  </div>
                  <div className="text-xs text-ink-2 truncate mt-1">
                    {threadSubject || '(无主题)'}
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft="check-circle-2"
                  onClick={() => markMut.mutate()}
                  loading={markMut.isPending}
                  className="shrink-0"
                >
                  标记已回
                </Button>
              </div>

              {/* 往来消息 */}
              {messagesQuery.isLoading ? (
                <div className="p-4 space-y-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : messagesQuery.isError ? (
                <div className="p-6 text-center">
                  <Icon name="alert-triangle" size="lg" className="text-bad mx-auto mb-2" />
                  <p className="text-sm text-ink-2">消息加载失败</p>
                  <button
                    type="button"
                    onClick={() => void messagesQuery.refetch()}
                    className="text-xs text-accent hover:underline mt-2"
                  >
                    重试
                  </button>
                </div>
              ) : messages.length === 0 ? (
                <div className="p-6">
                  <EmptyState
                    icon="mail"
                    title="这个会话还没有邮件"
                    description="客户来信后会自动出现在这里"
                  />
                </div>
              ) : (
                <div className="p-4 space-y-3 max-h-[55vh] lg:max-h-[60vh] overflow-y-auto">
                  {messages.map((m) => (
                    <MessageItem
                      key={m.id}
                      message={m}
                      expanded={expandedIds.has(m.id)}
                      onToggle={() => toggleExpand(m.id)}
                      translation={translations[m.id]}
                      translating={!!translatingIds[m.id]}
                      onTranslate={() => void translateMessage(m)}
                      readLocally={readIds.has(m.id)}
                    />
                  ))}
                </div>
              )}

              {/* 出站框 */}
              <ComposePanel
                text={composeText}
                subject={composeSubject}
                target={target}
                copied={copied}
                drafting={draftMut.isPending}
                translating={translateMut.isPending}
                onTextChange={setComposeText}
                onSubjectChange={setComposeSubject}
                onTargetChange={setTarget}
                onDraft={() => draftMut.mutate()}
                onTranslate={() => translateMut.mutate()}
                onCopy={(kind) =>
                  void copyToClipboard(kind, kind === 'body' ? composeText : composeSubject)
                }
                onOpenLark={openLark}
              />
            </div>
          )}
        </div>
      </div>

      {toast && <ToastBar toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

interface ThreadListPanelProps {
  filter: FilterKey;
  onFilterChange: (f: FilterKey) => void;
  searchInput: string;
  onSearchInputChange: (v: string) => void;
  threads: InboxThread[];
  counts?: { all: number; unread: number; pending: number; overdue: number };
  activeId: string | null;
  onSelect: (id: string) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/** 左栏: 搜索 + 筛选 tab + 线程列表 */
function ThreadListPanel(props: ThreadListPanelProps) {
  const {
    filter,
    onFilterChange,
    searchInput,
    onSearchInputChange,
    threads,
    counts,
    activeId,
    onSelect,
    isLoading,
    isError,
    onRetry,
  } = props;

  return (
    <div className="bg-surface border border-line rounded overflow-hidden">
      {/* 搜索 + 筛选 */}
      <div className="p-3 border-b border-line space-y-2.5">
        <Input
          size="sm"
          value={searchInput}
          onChange={(e) => onSearchInputChange(e.target.value)}
          placeholder="搜索客户 / 主题 / 邮箱"
          leftIcon="search"
          aria-label="搜索收件箱"
        />
        <div className="flex flex-wrap gap-1">
          {FILTER_TABS.map((t) => {
            const active = filter === t.key;
            const n = counts?.[t.key];
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => onFilterChange(t.key)}
                className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                  active
                    ? 'bg-accent text-white border-accent font-medium'
                    : 'bg-surface-2 text-ink-2 border-line hover:bg-surface'
                }`}
              >
                {t.label}
                {n != null ? <span className="ml-1 opacity-70">{n}</span> : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* 列表 */}
      {isLoading ? (
        <div className="p-3 space-y-3">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : isError ? (
        <div className="p-6 text-center">
          <Icon name="alert-triangle" size="lg" className="text-bad mx-auto mb-2" />
          <p className="text-sm text-ink-2">收件箱加载失败</p>
          <button
            type="button"
            onClick={onRetry}
            className="text-xs text-accent hover:underline mt-2"
          >
            重试
          </button>
        </div>
      ) : threads.length === 0 ? (
        <EmptyState
          icon="mail"
          title="收件箱是空的"
          description="Lark 邮箱同步器把来信推过来后, 这里就有内容了"
        />
      ) : (
        <ul className="max-h-[60vh] lg:max-h-[calc(100vh-260px)] overflow-y-auto">
          {threads.map((t) => {
            const active = t.inquiryId === activeId;
            return (
              <li key={t.inquiryId}>
                <button
                  type="button"
                  onClick={() => onSelect(t.inquiryId)}
                  aria-current={active ? 'true' : undefined}
                  className={`w-full text-left px-3 py-2.5 border-b border-line transition-colors ${
                    active ? 'bg-accent-soft' : 'hover:bg-surface-2'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {t.unread && (
                      <span
                        className="mt-1.5 w-2 h-2 rounded-full bg-accent shrink-0"
                        aria-label="未读"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span
                          className={`text-sm truncate ${
                            t.unread ? 'font-semibold text-ink' : 'font-medium text-ink-2'
                          }`}
                        >
                          {t.customerName || '(未知客户)'}
                        </span>
                        <span className="text-[10px] text-ink-3 shrink-0">
                          {fmtRelative(t.lastMessageAt)}
                        </span>
                      </div>
                      <div className="text-xs text-ink truncate mt-0.5">
                        {t.subject || '(无主题)'}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {t.customerCountry && (
                          <span className="text-[10px] text-ink-3">{t.customerCountry}</span>
                        )}
                        {t.overdue ? (
                          <span className="tag bg-bad/10 text-bad border-bad/30 font-medium">
                            逾期 {t.daysWaiting} 天
                          </span>
                        ) : t.pending ? (
                          <span className="tag bg-warn/10 text-warn border-warn/30">待回</span>
                        ) : null}
                        <span className="text-[10px] text-ink-3">{t.messageCount} 条</span>
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface MessageItemProps {
  message: InboxMessage;
  expanded: boolean;
  onToggle: () => void;
  translation?: string;
  translating: boolean;
  onTranslate: () => void;
  readLocally: boolean;
}

/** 单条邮件: 原文 (超 3 行折叠) + 翻译成中文 */
function MessageItem(props: MessageItemProps) {
  const { message, expanded, onToggle, translation, translating, onTranslate, readLocally } = props;
  const isInbound = message.direction === 'inbound';
  const body = message.bodyOriginal ?? '';
  const long = isLongBody(body);
  const zh = translation ?? message.bodyZh;
  // 契约里 attachmentNames 是数组, 但后端漏字段时别让整页崩掉
  const attachmentNames = message.attachmentNames ?? [];

  return (
    <div
      className={`rounded border p-3 ${
        isInbound ? 'bg-surface-2 border-line' : 'bg-accent-soft border-accent/30'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 text-[10px] text-ink-3 mb-1.5">
        <span className="font-medium text-ink-2">{isInbound ? '客户来信' : '我的回复'}</span>
        <span>{fmtMsgTime(message.createdAt)}</span>
        {isInbound && !message.isRead && !readLocally && (
          <span className="inline-flex items-center gap-1 text-accent">
            <span className="w-1.5 h-1.5 rounded-full bg-accent" aria-hidden />
            未读
          </span>
        )}
        {message.hasAttachments && (
          <span>📎 {attachmentNames.length > 0 ? attachmentNames.join('、') : '附件'}</span>
        )}
        {message.langDetected && <span>{message.langDetected.toUpperCase()}</span>}
      </div>

      <div
        className={`text-sm text-ink whitespace-pre-wrap break-words ${
          long && !expanded ? 'line-clamp-3' : ''
        }`}
      >
        {body || '(无正文)'}
      </div>
      {long && (
        <button
          type="button"
          onClick={onToggle}
          className="mt-1 text-[10px] text-accent hover:underline"
        >
          {expanded ? '收起' : '展开'}
        </button>
      )}

      {isInbound && (
        <div className="mt-2">
          {zh ? (
            <div className="bg-surface border-l-2 border-accent rounded px-2.5 py-2">
              <div className="text-[10px] text-ink-3 mb-1">中文译文</div>
              <div className="text-sm text-ink-2 whitespace-pre-wrap break-words">{zh}</div>
            </div>
          ) : (
            <button
              type="button"
              onClick={onTranslate}
              disabled={translating}
              className="px-2 py-1 text-[10px] border border-line rounded text-ink-2 hover:bg-surface disabled:opacity-50"
            >
              {translating ? '翻译中…' : '翻译成中文'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface ComposePanelProps {
  text: string;
  subject: string;
  target: DraftTarget;
  copied: 'body' | 'subject' | null;
  drafting: boolean;
  translating: boolean;
  onTextChange: (v: string) => void;
  onSubjectChange: (v: string) => void;
  onTargetChange: (v: DraftTarget) => void;
  onDraft: () => void;
  onTranslate: () => void;
  onCopy: (kind: 'body' | 'subject') => void;
  onOpenLark: () => void;
}

/**
 * 出站框: 中文写意图 → AI 出稿 / 翻译 → 复制正文与主题 → 去 Lark 发送
 * (发送动作留在 Lark, 这里只负责把内容准备好)
 */
function ComposePanel(props: ComposePanelProps) {
  const {
    text,
    subject,
    target,
    copied,
    drafting,
    translating,
    onTextChange,
    onSubjectChange,
    onTargetChange,
    onDraft,
    onTranslate,
    onCopy,
    onOpenLark,
  } = props;

  return (
    <div className="border-t border-line p-4 space-y-2.5 bg-surface">
      <Input
        size="sm"
        value={subject}
        onChange={(e) => onSubjectChange(e.target.value)}
        placeholder="主题 (AI 出稿会自动填)"
        aria-label="主题"
      />
      <Textarea
        size="sm"
        rows={4}
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder="用中文写你的意思…"
        aria-label="正文"
      />

      <div className="flex flex-wrap items-center gap-2">
        <Select
          size="sm"
          options={TARGET_OPTIONS}
          value={target}
          onChange={(e) => onTargetChange(e.target.value as DraftTarget)}
          containerClassName="w-28"
          aria-label="目标语言"
        />
        <Button
          variant="primary"
          size="sm"
          iconLeft="sparkles"
          onClick={onDraft}
          loading={drafting}
          disabled={!text.trim()}
        >
          AI 出稿
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onTranslate}
          loading={translating}
          disabled={!text.trim()}
        >
          翻译
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          iconLeft="copy"
          onClick={() => onCopy('body')}
          disabled={!text.trim()}
        >
          {copied === 'body' ? '已复制' : '复制正文'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconLeft="copy"
          onClick={() => onCopy('subject')}
          disabled={!subject.trim()}
        >
          {copied === 'subject' ? '已复制' : '复制主题'}
        </Button>
        <Button variant="ghost" size="sm" iconRight="external-link" onClick={onOpenLark}>
          在 Lark 打开
        </Button>
      </div>
    </div>
  );
}

/** 右下角轻提示 */
function ToastBar({ toast, onClose }: { toast: ToastState; onClose: () => void }) {
  const tone =
    toast.kind === 'success' ? 'bg-good' : toast.kind === 'error' ? 'bg-bad' : 'bg-accent';
  return (
    <button
      type="button"
      className={`fixed bottom-4 right-4 px-4 py-2 rounded text-sm shadow-lg z-50 cursor-pointer ${tone} text-white`}
      onClick={onClose}
    >
      {toast.msg}
    </button>
  );
}
